import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { NavigationService } from '@treasury/core/navigation';
import { CompanyService } from '@treasury/domain/backoffice/services/company';
import { CompanyUserApprovalSettings } from '@treasury/domain/backoffice/services/company/entities/company-user-approval-settings.entity';
import '@treasury/presentation/components/jh-ui/tm-button';
import '@treasury/presentation/components/jh-ui/tm-checkbox';
import '@treasury/presentation/components/jh-ui/tm-switch';
import {
    SideDrawerButtonPosture,
    TmSideDrawerService,
} from '@treasury/presentation/services/side-drawer';
import { TmSideDrawerContent } from '@treasury/presentation/services/side-drawer/side-drawer-content';
import { TmToastPosture, TmToastService } from '@treasury/presentation/services/toast';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export const tagName = 'tm-bo-user-settings';

@customElement(tagName)
export class UserSettingsDrawer extends TmSideDrawerContent {
    constructor() {
        super();
        this.drawerProvider.provideButtons([
            {
                label: 'Cancel',
                reason: DynamicComponentExitReason.Cancel,
                posture: SideDrawerButtonPosture.Tertiary,
            },
            {
                label: 'Save',
                reason: DynamicComponentExitReason.Confirm,
                posture: SideDrawerButtonPosture.Primary,
                checkEnabled: () => this.isDirty,
                onClick: close => {
                    this.handleSaveClick();
                    close();
                },
            },
        ]);
    }

    public static title = 'User’s settings';

    @property({ type: Number })
    public companyId?: number;

    @InjectProperty()
    declare private readonly navigationService: NavigationService;

    @InjectProperty()
    declare private readonly toastService: TmToastService;

    @InjectProperty()
    declare private readonly companyService: CompanyService;

    @InjectProperty()
    declare private readonly sideDrawerService: TmSideDrawerService;

    @state()
    private userApprovalSettings?: CompanyUserApprovalSettings;

    @state()
    private loading = false;

    @state()
    private isDirty = false;

    @state()
    private isSaving = false;

    public render() {
        if (this.loading) {
            return html`<div class="flex justify-center py-8"><jha-progress></jha-progress></div>`;
        }
        return html`<div class="mb-10">
            <hr />
            <h2 class="text-sm font-medium mt-3 mb-8">
                Manage what approvals are needed when new users are<br />created.
            </h2>
            <h2 class="text-md font-medium mb-5">Users created in Treasury channel</h2>
            <tm-wc-checkbox
                class="mb-5"
                .initial=${this.userApprovalSettings?.channelApproveChannel}
                name="channelApproveChannel"
                label="Require approval by someone at their company."
                @value-changed=${this.handleSwitchChange}
            ></tm-wc-checkbox>
            <tm-wc-checkbox
                class="mb-5"
                .initial=${this.userApprovalSettings?.channelApproveBackOffice}
                name="channelApproveBackOffice"
                label="Require approval by someone in Treasury back office."
                @value-changed=${this.handleSwitchChange}
            ></tm-wc-checkbox>
            <h2 class="text-md font-medium mb-5 mt-8">Users created in Treasury back office</h2>
            <tm-wc-checkbox
                class="mb-5"
                .initial=${this.userApprovalSettings?.backOfficeApproveBackOffice}
                name="backOfficeApproveBackOffice"
                label="Require approval by someone in Treasury back office."
                @value-changed=${this.handleSwitchChange}
            ></tm-wc-checkbox>
        </div>`;
    }

    protected async firstUpdated() {
        this.loading = true;
        const errorMessage = 'Cannot load user settings, No company found.';
        if (!this.companyId) {
            throw new Error(errorMessage);
        }
        try {
            this.userApprovalSettings = await this.companyService.getCompanyUserApprovalSettings(
                this.companyId
            );
        } catch {
            this.toastService.show(errorMessage, TmToastPosture.Danger);
        } finally {
            this.loading = false;
        }
    }

    private async handleSaveClick() {
        this.isSaving = true;
        const errorMessage = "There was an issue saving the company's user approval settings.";
        if (!this.companyId || !this.userApprovalSettings) {
            throw new Error(errorMessage);
        }
        try {
            const settings = await this.companyService.saveCompanyUserApprovalSettings(
                this.companyId,
                this.userApprovalSettings
            );
            this.userApprovalSettings = settings;
            this.toastService.show(
                "Successfully save the company's user approval settings.",
                TmToastPosture.Success
            );
            setTimeout(() => {
                this.sideDrawerService.close();
            }, 400);
        } catch {
            this.toastService.show(errorMessage, TmToastPosture.Danger);
        } finally {
            this.isSaving = false;
        }
    }

    private handleSwitchChange(e: CustomEvent) {
        this.isDirty = true;
        const errorMessage = 'There was an issue applying your setting.';
        try {
            if (!this.userApprovalSettings) {
                throw new Error(errorMessage);
            }
            const target = e.target as HTMLInputElement;
            const name = target.getAttribute('name');
            if (!name) {
                throw new Error(errorMessage);
            }
            if (name in this.userApprovalSettings) {
                this.userApprovalSettings = new CompanyUserApprovalSettings({
                    ...this.userApprovalSettings.toDto(),
                    [name]: e.detail.value,
                });
            } else {
                throw new Error(errorMessage);
            }
        } catch {
            this.toastService.show(errorMessage, TmToastPosture.Danger, true);
        }
    }
}
